import type { CdToastKind } from '@ui';
import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';

/** Older toasts are dropped once more than this many are visible. */
export const MAX_TOASTS = 3;
/** Info, success and warning close after this; errors stay until closed. */
export const DEFAULT_TOAST_TIMEOUT = 5000;

export interface Toast {
  id: string;
  kind: CdToastKind;
  title: string;
  message?: string;
  /** Auto-close delay in ms; 0 keeps the toast open. */
  timeout: number;
  /** Epoch ms – shown as the toast's timestamp. */
  createdAt: number;
}

export interface ShowToastInput {
  kind: CdToastKind;
  title: string;
  message?: string;
  timeout?: number;
}

export interface ToastState {
  /** Newest first. */
  items: Toast[];
}

const initialState: ToastState = { items: [] };

export const toastSlice = createSlice({
  name: 'toast',
  initialState,
  reducers: {
    showToast: {
      reducer: (state, action: PayloadAction<Toast>) => {
        state.items = [action.payload, ...state.items].slice(0, MAX_TOASTS);
      },
      prepare: ({ timeout, ...input }: ShowToastInput) => ({
        payload: {
          ...input,
          id: nanoid(),
          createdAt: Date.now(),
          timeout: timeout ?? (input.kind === 'error' ? 0 : DEFAULT_TOAST_TIMEOUT),
        },
      }),
    },
    dismissToast: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((t) => t.id !== action.payload);
    },
    clearToasts: (state) => {
      state.items = [];
    },
  },
  selectors: {
    selectToasts: (state) => state.items,
  },
});

export const { showToast, dismissToast, clearToasts } = toastSlice.actions;
export const { selectToasts } = toastSlice.selectors;

type ToastOptions = Pick<ShowToastInput, 'timeout'>;
const make = (kind: CdToastKind) => (title: string, message?: string, options?: ToastOptions) =>
  showToast({ kind, title, message, ...options });

/**
 * Action creators for the four toast kinds:
 * `dispatch(toast.success('Saved', 'Permit updated'))`, or `store.dispatch(...)` outside React.
 */
export const toast = {
  info: make('info'),
  success: make('success'),
  warning: make('warning'),
  error: make('error'),
};
